import { Add, Remove } from "assets/icons/Common";
import { Col, IconButton } from "components/primitives";
import { useInterval } from "hooks/interval";
import { useState } from "react";
import styled from "styled-components";

interface Props<T> {
  value: T;
  onIncrement: () => void;
  onDecrement: () => void;
  showButtons: boolean;
  variant?: "primary" | "secondary";
  textColor?: string;
}

const minAutoCountDelay = 200;
const maxAutoCountDelay = 600;

export const AdjustableDigit: React.FC<Props<number | string>> = ({
  value,
  onIncrement,
  onDecrement,
  showButtons,
  variant = "primary",
  textColor = "var(--secondary)",
}) => {
  const [autoCountDelay, setAutoCountDelay] = useState(maxAutoCountDelay);
  const [shouldIncrement, setShouldIncrement] = useState<boolean>(true);
  const [setRunInterval] = useInterval(
    () => {
      // Speed up the longer the button is held
      setAutoCountDelay(Math.max(autoCountDelay - 200, minAutoCountDelay));
      runChange(shouldIncrement);
    },
    autoCountDelay,
    false,
  );

  const runChange = (increment: boolean) => (increment ? onIncrement() : onDecrement());

  const onPointerDown = (increment: boolean) => {
    runChange(increment);
    setShouldIncrement(increment);
    setRunInterval(true);
  };

  const onPointerUp = () => {
    setRunInterval(false);
    setAutoCountDelay(maxAutoCountDelay);
  };

  const isPrimary = variant === "primary";
  return (
    <OuterContainer width={isPrimary ? "28cqw" : "20cqw"}>
      <AdjustButton
        visible={showButtons}
        withShadow={false}
        Icon={Add}
        onPointerDown={() => onPointerDown(true)}
        onPointerUp={onPointerUp}
      />
      <TextContainer>
        <Text fontSize={isPrimary ? "16cqw" : "10cqw"} color={isPrimary ? textColor : "gray"}>
          {String(value).padStart(2, "0") || 0}
        </Text>
      </TextContainer>
      <AdjustButton
        visible={showButtons}
        withShadow={false}
        Icon={Remove}
        onPointerDown={() => onPointerDown(false)}
        onPointerUp={onPointerUp}
      />
    </OuterContainer>
  );
};

const OuterContainer = styled.div<{ width: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: ${(props) => props.width};
`;

const TextContainer = styled(Col)`
  height: 16cqw;
  justify-content: flex-end;
  width: 100%;
  align-items: center;
`;

const Text = styled.p<{ fontSize: string; color: string }>`
  font-size: ${(props) => props.fontSize};
  line-height: ${(props) => props.fontSize};
  color: ${(props) => props.color};
`;

const AdjustButton = styled(IconButton)<{ visible: boolean }>`
  width: 12cqw;
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  padding: 3.5cqw;
`;
